import { useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { Video } from "lucide-react";
import { AnalyticsService } from "@/services/analytics";
import {
  GenerateButton,
  PromptInput,
  UpgradePlansModal,
  AuthModal,
  MobileBottomBar,
} from "@/components/ui";
import { DimensionsDropdown } from "./dimensions-dropdown";
import { PiecanNotice } from "@/components/features/piecan-notice";
import { useStartGeneration, useActiveGenerations } from "@/hooks";
import { useUserStore, hasAccessToPlanLevel } from "@/stores/user-store";

const T2V_CREDIT_COST = 120;

export function TextToVideoForm() {
  const { isSignedIn } = useUser();
  const { credits, plan } = useUserStore();
  const { startGeneration } = useStartGeneration();
  const { hasActiveGeneration } = useActiveGenerations();
  const [prompt, setPrompt] = useState("");
  const [orientation, setOrientation] = useState<"horizontal" | "vertical">(
    "horizontal",
  );
  const [upgradeModal, setUpgradeModal] = useState<{
    open: boolean;
    preset: "no-credits" | "concurrent-limit";
    creditsNeeded?: number;
  }>({ open: false, preset: "no-credits" });
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Check if user has Pro or Ultimate (unlimited concurrent generations)
  const hasUnlimitedConcurrent = hasAccessToPlanLevel(plan, "pro");

  const handleGenerate = () => {
    if (!prompt.trim()) return;

    // Check if user is signed in
    if (!isSignedIn) {
      setShowAuthModal(true);
      return;
    }

    // Check if user has enough credits
    if (credits < T2V_CREDIT_COST) {
      AnalyticsService.trackOutOfCredits({
        credits_remaining: credits,
        credits_required: T2V_CREDIT_COST,
        model_attempted: 'wan_t2v',
        generation_type: 'video',
        is_paid_user: plan !== 'free',
        user_plan: plan,
      });
      setUpgradeModal({
        open: true,
        preset: "no-credits",
        creditsNeeded: T2V_CREDIT_COST,
      });
      return;
    }

    // Check concurrent generation limit for non-Pro users
    if (hasActiveGeneration && !hasUnlimitedConcurrent) {
      setUpgradeModal({ open: true, preset: "concurrent-limit" });
      return;
    }

    const width = orientation === "horizontal" ? 1280 : 720;
    const height = orientation === "horizontal" ? 720 : 1280;

    // Navigate to generation page with booting screen
    startGeneration({
      modelId: "wan_2_6_t2v_5s",
      prompt: prompt.trim(),
      type: "video",
      generateParams: {
        model_id: "wan_2_6_t2v_5s",
        prompt: prompt.trim(),
        width,
        height,
      },
    });
  };

  const isGenerating = false; // No longer tracking local mutation state

  return (
    <>
      <div className="space-y-4 pb-24 md:pb-0">
        {/* Partner upsell — points upload-capable users to PieCan for the
            image-upload flow. Compact so the composer stays primary. */}
        <PiecanNotice variant="compact" source="t2v_form" />

        <PromptInput
          value={prompt}
          onChange={setPrompt}
          placeholder="Describe your video... A serene mountain landscape at sunset, with clouds slowly drifting across the sky..."
          disabled={isGenerating}
        />

        {/* Controls Row */}
        <div className="flex flex-col md:flex-row gap-3">
          {/* Dimensions Dropdown with gradient background */}
          <div className="relative rounded-xl p-[1px] bg-gradient-to-br from-white/[0.12] via-white/[0.04] to-white/[0.08] w-full md:w-[200px] shrink-0">
            <DimensionsDropdown
              value={orientation}
              onChange={setOrientation}
              disabled={isGenerating}
            />
          </div>
          {/* Generate Button - Desktop only */}
          <div className="hidden md:block flex-1 self-stretch">
            <GenerateButton
              disabled={!prompt.trim() || isGenerating}
              className="w-full h-full"
              onClick={handleGenerate}
            >
              <Video className="size-5" />
              Generate Video ({T2V_CREDIT_COST} credits)
            </GenerateButton>
          </div>
        </div>
      </div>

      {/* Fixed bottom bar - Mobile only */}
      <MobileBottomBar>
        <GenerateButton
          disabled={!prompt.trim() || isGenerating}
          className="w-full"
          onClick={handleGenerate}
        >
          <Video className="size-5" />
          Generate Video ({T2V_CREDIT_COST} credits)
        </GenerateButton>
      </MobileBottomBar>

      <UpgradePlansModal
        isOpen={upgradeModal.open}
        onClose={() => setUpgradeModal({ open: false, preset: "no-credits" })}
        preset={upgradeModal.preset}
        presetContext={{
          creditsNeeded: upgradeModal.creditsNeeded,
          currentCredits: credits,
        }}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
}
